import Phaser from 'phaser';
import { Direction } from '@shared/types/entity';
import { addGridSpriteSheet } from './gridSpriteSheet';

/**
 * Sprites de escudo (shield):
 * - Misma estructura lógica que los body:
 *   - 4 filas (abajo, arriba, izquierda, derecha)
 *   - 6 columnas (en left/right la última suele estar vacía)
 * - PERO el tamaño real de cada frame se calcula a partir
 *   del tamaño del archivo (ancho/6, alto/4).
 */
const SHIELD_COLUMNS = 6;
const SHIELD_ROWS = 4;

type DirectionAnimKey = 'down' | 'up' | 'left' | 'right';

function getDirectionKey(dir: Direction): DirectionAnimKey {
  switch (dir) {
    case Direction.DOWN:
      return 'down';
    case Direction.UP:
      return 'up';
    case Direction.LEFT:
      return 'left';
    case Direction.RIGHT:
      return 'right';
    default:
      return 'down';
  }
}

export function getShieldSpriteKey(shieldId: number): string {
  return `shield${shieldId}`;
}

function frameName(row: number, col: number): string {
  return `r${row}c${col}`;
}

// Devuelve el nombre de frame para el frame "idle" (columna 0) de una dirección.
export function getShieldIdleFrame(dir: Direction): string {
  let row = 0;
  switch (dir) {
    case Direction.DOWN:
      row = 0;
      break;
    case Direction.UP:
      row = 1;
      break;
    case Direction.LEFT:
      row = 2;
      break;
    case Direction.RIGHT:
      row = 3;
      break;
    default:
      row = 0;
      break;
  }

  return frameName(row, 0);
}

/**
 * Pre-carga en Phaser los spritesheet de shield para una lista de IDs.
 * Asume que los archivos existen en `client/assets/shield/shieldN.png`.
 */
export function preloadShieldSprites(scene: Phaser.Scene, shieldIds: number[]): void {
  const anyScene = scene as any;

  // Adjuntar handler global de FILE_COMPLETE una sola vez por escena
  if (!anyScene.__shieldSheetLoaderAttached) {
    anyScene.__shieldSheetLoaderAttached = true;

    scene.load.on(Phaser.Loader.Events.FILE_COMPLETE, (fileKey: string, _type: string) => {
      if (typeof fileKey !== 'string') return;
      // Esperamos claves del tipo "shieldN-img"
      if (!fileKey.startsWith('shield') || !fileKey.endsWith('-img')) return;

      const idStr = fileKey.slice(6, -4);
      const id = Number(idStr);
      if (!Number.isFinite(id)) return;

      const sheetKey = getShieldSpriteKey(id);
      // Si ya existe el spritesheet lógico, no hacemos nada
      if (scene.textures.exists(sheetKey)) return;

      const srcTex = scene.textures.get(fileKey);
      if (!srcTex) return;

      const source = srcTex.getSourceImage() as HTMLImageElement;
      if (!source) return;

      // Cortar la grilla 6×4 con bordes redondeados (frames r{row}c{col}). Bordes
      // redondeados → sin drift si el PNG no es múltiplo exacto. Las filas 2/3 sólo
      // usan cols 0..4 en el render; registrar r2c5/r3c5 de más es inofensivo
      // (nunca se referencian).
      addGridSpriteSheet(scene, sheetKey, source, SHIELD_COLUMNS, SHIELD_ROWS, (row, col) =>
        frameName(row, col)
      );
    });
  }

  for (const id of shieldIds) {
    const sheetKey = getShieldSpriteKey(id);
    if (scene.textures.exists(sheetKey)) continue;

    const imgKey = `${sheetKey}-img`;
    // Evitar recargar la misma imagen
    if (scene.textures.exists(imgKey)) continue;

    scene.load.image(imgKey, `assets/shield/${sheetKey}.png`);
  }
}

// Helper para sincronizar frames por fila/columna desde fuera si se necesita
export function getShieldFrameFromBodyIndex(bodyFrameIndex: number, bodyColumns: number): string {
  const row = Math.floor(bodyFrameIndex / bodyColumns);
  const col = bodyFrameIndex % bodyColumns;
  return frameName(row, col);
}

export function getShieldAnimKey(shieldId: number, dir: Direction): string {
  const dirKey = getDirectionKey(dir);
  return `shield${shieldId}_${dirKey}`;
}/**
 * Crea las animaciones de caminar para cada shield y dirección.
 * DEBE llamarse después de que los spritesheets estén listos.
 */
export function createShieldAnimations(
  scene: Phaser.Scene,
  shieldIds: number[],
  frameRate: number = 6
): void {
  for (const id of shieldIds) {
    const key = getShieldSpriteKey(id);    // Verificar que la textura existe
    if (!scene.textures.exists(key)) continue;    // Down (fila 0, columnas 0..5)
    ensureShieldAnim(
      scene,
      getShieldAnimKey(id, Direction.DOWN),
      key,
      0,
      0,
      5,
      frameRate
    );    // Up (fila 1, columnas 0..5)
    ensureShieldAnim(
      scene,
      getShieldAnimKey(id, Direction.UP),
      key,
      1,
      0,
      5,
      frameRate
    );    // Left (fila 2, columnas 0..4)
    ensureShieldAnim(
      scene,
      getShieldAnimKey(id, Direction.LEFT),
      key,
      2,
      0,
      4,
      frameRate
    );    // Right (fila 3, columnas 0..4)
    ensureShieldAnim(
      scene,
      getShieldAnimKey(id, Direction.RIGHT),
      key,
      3,
      0,
      4,
      frameRate
    );
  }
}function ensureShieldAnim(
  scene: Phaser.Scene,
  animKey: string,
  spriteKey: string,
  row: number,
  colStart: number,
  colEnd: number,
  frameRate: number
): void {
  if (scene.anims.exists(animKey)) return;  const frames: Phaser.Types.Animations.AnimationFrame[] = [];
  for (let col = colStart; col <= colEnd; col++) {
    const frameName = `r${row}c${col}`;
    if (scene.textures.get(spriteKey).has(frameName)) {
      frames.push({ key: spriteKey, frame: frameName });
    }
  }  if (frames.length > 0) {
    scene.anims.create({
      key: animKey,
      frames: frames,
      frameRate,
      repeat: -1,
    });
  }
}